import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback } from 'react';

import {
  AllDocumentTypes,
  DocumentApiResponse,
  DocumentDocument,
  DocumentSortField,
  DocumentStatus,
  DocumentUploadEvent,
  DocumentUploadEventType,
  DocumentUploadInitRequest,
  DocumentUploadJobResponse,
} from 'types';

import { api } from './api';

/**
 * LaunchDarkly kill-switch for v2 document handling.
 *
 * Default is `false` (v1 routes) so that flipping the flag off in
 * LaunchDarkly — or losing the LD connection — reverts to the legacy
 * multipart upload + streaming-URL path. The v2 codepath is opt-in.
 */
export const useDocumentsV2Enabled = (): boolean => {
  const { documentsV2Enabled = false } = useFlags();
  return documentsV2Enabled;
};

const docsPath = (v2: boolean, accountId: string, suffix = ''): string =>
  v2 ? `v2/accounts/${accountId}/documents${suffix}` : `accounts/${accountId}/documents${suffix}`;

const SSE_TIMEOUT_MS = 5 * 60 * 1000;
const POLLING_INTERVAL_MS = 2_000;

/**
 * Listen on the SSE channel for upload progress events.
 *
 * Event flow:
 *   - `complete` is terminal for the upload — documents are persisted with real
 *     ids and original `viewUrls`, which is everything the caller needs to
 *     unblock the user. Image variants (thumbnails/previews) may still be
 *     generating; those land asynchronously via `watchDocumentVariants` and a
 *     `getDocuments` invalidation, so we resolve here and do NOT wait.
 *   - `imageVariantsReady` ALSO resolves the upload. The progress endpoint
 *     surfaces the latest cached event, so if variant generation finishes
 *     before we observe `complete` (fast workers, or a poll/SSE replay landing
 *     on the later state) we may see `imageVariantsReady` first. It carries the
 *     same `documents`, so resolving on it avoids hanging until the timeout. The
 *     point of Phase 1 is to not WAIT for variants, not to ignore them.
 *   - `failed` is terminal on error — resolves with the error.
 *
 * On EventSource error we fall back to polling, so the caller never hangs.
 */
async function awaitUploadProgress(
  accountId: string,
  jobId: string,
): Promise<{ data: DocumentApiResponse[] } | { error: { status: string; error: string } }> {
  if (typeof EventSource === 'undefined') {
    return pollUploadProgress(accountId, jobId);
  }

  return new Promise((resolve) => {
    const url = `/api/v2/accounts/${accountId}/documents/upload-progress?jobId=${encodeURIComponent(jobId)}`;
    const sse = new EventSource(url, { withCredentials: true });

    const timeout = setTimeout(() => {
      sse.close();
      resolve({ error: { status: 'TIMEOUT_ERROR', error: 'Upload timed out' } });
    }, SSE_TIMEOUT_MS);

    sse.onmessage = (e: MessageEvent) => {
      try {
        const event = JSON.parse(e.data) as DocumentUploadEvent;
        if (
          event.type === DocumentUploadEventType.complete ||
          event.type === DocumentUploadEventType.imageVariantsReady
        ) {
          // Resolve on whichever arrives first; both carry the documents.
          clearTimeout(timeout);
          sse.close();
          resolve({ data: event.documents });
        } else if (event.type === DocumentUploadEventType.failed) {
          clearTimeout(timeout);
          sse.close();
          resolve({ error: { status: 'CUSTOM_ERROR', error: event.error } });
        }
      } catch {
        // Ignore malformed messages
      }
    };

    sse.onerror = () => {
      clearTimeout(timeout);
      sse.close();
      // Fall back to polling on SSE error.
      pollUploadProgress(accountId, jobId).then(resolve);
    };
  });
}

async function pollUploadProgress(
  accountId: string,
  jobId: string,
): Promise<{ data: DocumentApiResponse[] } | { error: { status: string; error: string } }> {
  const start = Date.now();

  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (Date.now() - start > SSE_TIMEOUT_MS) {
      return { error: { status: 'TIMEOUT_ERROR', error: 'Upload timed out' } };
    }

    try {
      const res = await fetch(`/api/v2/accounts/${accountId}/documents/jobs/${jobId}`, {
        credentials: 'include',
      });
      if (res.ok) {
        const event = (await res.json()) as DocumentUploadEvent;
        if (
          event.type === DocumentUploadEventType.complete ||
          event.type === DocumentUploadEventType.imageVariantsReady
        ) {
          // Resolve on whichever the endpoint surfaces; both carry the documents.
          return { data: event.documents };
        } else if (event.type === DocumentUploadEventType.failed) {
          return { error: { status: 'CUSTOM_ERROR', error: event.error } };
        }
      }
    } catch {
      // Transient error; keep polling.
    }

    await new Promise((r) => setTimeout(r, POLLING_INTERVAL_MS));
  }
}

async function putFileToSignedUrl(file: File, uploadUrl: string, contentType?: string): Promise<void> {
  // The signed URL is bound to the exact contentType the server signed with.
  // Always prefer that (the server picks it from the extension whitelist) over
  // `file.type`, since browsers report `.heic` files inconsistently and on
  // Windows often as an empty string — which would 403 against the signature.
  const headerContentType = contentType || file.type || 'application/octet-stream';
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': headerContentType },
  });
  if (!res.ok) {
    throw new Error(`Failed to PUT file ${file.name} to GCS: ${res.status}`);
  }
}

const buildQueryUrl = (baseUrl: string, filters: Record<string, any[] | DocumentSortField | undefined>) => {
  const filterArr = Object.keys(filters)
    .map((filterKey) => {
      if (filters[filterKey]) {
        return `${filterKey}=${filters[filterKey]}`;
      }
      return null;
    })
    .filter((filterStr) => !!filterStr);

  if (filterArr.length) {
    return `${baseUrl}?${filterArr.join('&')}`;
  }

  return baseUrl;
};

export const documentsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getDocuments: build.query<
      DocumentDocument[],
      {
        accountId: string;
        documentTypeFilter?: Array<AllDocumentTypes>;
        documentStatusFilter?: Array<DocumentStatus>;
        sort?: DocumentSortField;
        v2?: boolean;
      }
    >({
      query: ({ accountId, documentTypeFilter, documentStatusFilter, sort, v2 = false }) => {
        return {
          url: buildQueryUrl(docsPath(v2, accountId), {
            filter: documentTypeFilter,
            status: documentStatusFilter,
            sort,
          }),
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.flatMap(({ id, type }) => [
                { type: 'DocumentDocument' as const, id: id?.toString() },
                { type: 'DocumentDocument' as const, id: type },
              ]),
              { type: 'DocumentDocument', id: 'LIST' },
            ]
          : [{ type: 'DocumentDocument', id: 'LIST' }],
    }),
    getDocumentByAccountAndDocumentId: build.query<
      DocumentDocument,
      { accountId: string; documentId: string; v2?: boolean }
    >({
      query: ({ accountId, documentId, v2 = false }) => docsPath(v2, accountId, `/${documentId}`),
      providesTags: (_result, _error, { documentId }) => [{ type: 'DocumentDocument', id: documentId }],
    }),
    updateDocumentStatus: build.mutation<
      DocumentDocument,
      {
        id: string;
        accountId: string;
        status: DocumentStatus;
        rejectionReasons?: string[];
        rejectionNote?: string;
        v2?: boolean;
      }
    >({
      query: ({ id, accountId, status, rejectionNote, rejectionReasons, v2 = false }) => ({
        url: docsPath(v2, accountId, `/${id}`),
        method: 'PATCH',
        body: { status, rejectionNote, rejectionReasons },
      }),
      invalidatesTags: (result, _error, { id, accountId }) => [
        { type: 'DocumentDocument', id },
        { type: 'DocumentDocument', id: 'LIST' },
        { type: 'DocumentDocument', id: 'REJECTED' },
        ...(result?.type ? [{ type: 'DocumentDocument' as const, id: result.type }] : []),
        { type: 'AccountMilestones', id: 'AccountMilestones' },
        { type: 'AccountProgress', id: 'AccountProgress' },
        { type: 'Applications', id: 'LIST' },
        { type: 'QueueTasks', id: 'LIST' },
        { type: 'Queues', accountId: accountId },
        { type: 'Quotes', id: 'LIST' },
        { type: 'Tasks', id: 'LIST' },
        { type: 'AccountMilestones', id: accountId },
        { type: 'Accounts', id: accountId },
      ],
    }),
    updateDocumentType: build.mutation<
      DocumentDocument,
      { id: string; accountId: string; type: DocumentDocument['type']; v2?: boolean }
    >({
      query: ({ id, accountId, type, v2 = false }) => ({
        url: docsPath(v2, accountId, `/${id}`),
        method: 'PATCH',
        body: { type },
      }),
      invalidatesTags: (result, _error, { id, accountId, type }) => [
        { type: 'DocumentDocument', id },
        { type: 'DocumentDocument', id: 'LIST' },
        ...(result?.type && result.type !== type ? [{ type: 'DocumentDocument' as const, id: result.type }] : []),
        { type: 'Accounts', id: accountId },
      ],
    }),
    archiveDocument: build.mutation<
      DocumentDocument,
      { id: string; accountId: string; invalidateAllTags: boolean; v2?: boolean }
    >({
      query: ({ id, accountId, v2 = false }) => ({
        url: docsPath(v2, accountId, `/${id}`),
        method: 'DELETE',
      }),
      invalidatesTags: (result, _error, { accountId, id, invalidateAllTags }) =>
        invalidateAllTags
          ? [
              { type: 'DocumentDocument', id },
              { type: 'DocumentDocument', id: 'LIST' },
              { type: 'QueueTasks', id: 'LIST' },
              { type: 'Queues', accountId: accountId },
              { type: 'Tasks', id: 'LIST' },
              { type: 'AccountMilestones', id: accountId },
              { type: 'Accounts', id: accountId },
              ...(result?.type ? [{ type: 'DocumentDocument' as const, id: result.type }] : []),
            ]
          : [
              { type: 'DocumentDocument', id },
              { type: 'DocumentDocument', id: 'LIST' },
              { type: 'Queues', accountId: accountId },
              ...(result?.type ? [{ type: 'DocumentDocument' as const, id: result.type }] : []),
            ],
    }),
    uploadDocument: build.mutation<
      { documents: DocumentDocument[]; jobId?: string },
      { accountId: string; formData: FormData; invalidateAllTags: boolean; v2?: boolean }
    >({
      queryFn: async ({ accountId, formData, v2 = false }) => {
        if (!v2) {
          // v1 multipart flow: POST the FormData directly and rely on the
          // server to upload to GCS and return persisted documents.
          const res = await fetch(`/api/accounts/${accountId}/documents`, {
            method: 'POST',
            body: formData,
            credentials: 'include',
          });
          if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            return { error: { status: res.status, data } } as any;
          }
          const documents = (await res.json()) as DocumentDocument[];
          // v1 has no async variant generation, so there is no jobId to watch.
          return { data: { documents, jobId: undefined } };
        }

        // v2 JSON flow: extract files + fields from FormData and use the
        // signed-URL upload path.
        const files: File[] = [];
        const fields: Record<string, string> = {};
        formData.forEach((value, key) => {
          if (value instanceof File) {
            files.push(value);
          } else if (typeof value === 'string') {
            fields[key] = value;
          }
        });

        // internalReferences arrives from FormData as a JSON-stringified array
        // (see SystemSection.tsx). For the v2 JSON body, parse it back to a real
        // array so the wire format matches the typed contract. Fall back to
        // omitting the field if parsing fails — the server is defensive too.
        let internalReferences: { fieldname: string; value: string }[] | undefined;
        if (typeof fields.internalReferences === 'string') {
          try {
            const parsed = JSON.parse(fields.internalReferences);
            if (Array.isArray(parsed)) internalReferences = parsed;
          } catch {
            // ignore — internalReferences stays undefined
          }
        }

        const initBody: DocumentUploadInitRequest = {
          files: files.map((f) => ({ filename: f.name, contentType: f.type, size: f.size })),
          type: fields.type,
          fields: {
            ...fields,
            ...(internalReferences !== undefined && { internalReferences }),
          } as DocumentUploadInitRequest['fields'],
        };

        const initRes = await fetch(`/api/v2/accounts/${accountId}/documents`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(initBody),
          credentials: 'include',
        });

        if (!initRes.ok) {
          const data = await initRes.json().catch(() => ({}));
          return { error: { status: initRes.status, data } } as any;
        }

        const initData = (await initRes.json()) as DocumentUploadJobResponse;
        const { jobId, files: signedFiles = [] } = initData;

        // Direct PUT each file to GCS in parallel using its signed URL.
        try {
          const filesByName = new Map(files.map((f) => [f.name, f]));
          await Promise.all(
            signedFiles.map((sf) => {
              const file = filesByName.get(sf.filename);
              if (!file) {
                throw new Error(`No matching File for filename ${sf.filename}`);
              }
              return putFileToSignedUrl(file, sf.uploadUrl, sf.contentType);
            }),
          );
        } catch (err) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: err instanceof Error ? err.message : 'GCS upload failed',
            },
          } as any;
        }

        const progress = await awaitUploadProgress(accountId, jobId);
        if ('error' in progress) {
          return { error: progress.error } as any;
        }
        // DocumentApiResponse extends DocumentDocument, so no cast is needed.
        return { data: { documents: progress.data, jobId } };
      },
      invalidatesTags: (result, _error, { accountId, invalidateAllTags }) =>
        invalidateAllTags
          ? [
              { type: 'DocumentDocument', id: 'LIST' },
              ...(result?.documents?.map((doc) => ({ type: 'DocumentDocument' as const, id: doc.type })) || []),
              { type: 'QueueTasks', id: 'LIST' },
              { type: 'Queues', accountId: accountId },
              { type: 'Tasks', id: 'LIST' },
              { type: 'AccountMilestones', id: accountId },
              { type: 'Accounts', id: accountId },
            ]
          : [
              { type: 'DocumentDocument', id: 'LIST' },
              ...(result?.documents?.map((doc) => ({ type: 'DocumentDocument' as const, id: doc.type })) || []),
              { type: 'Queues', accountId: accountId },
            ],
    }),
    refreshDocumentUrls: build.mutation<
      DocumentApiResponse,
      { accountId: string; documentId: string; v2?: boolean }
    >({
      query: ({ accountId, documentId, v2 = false }) =>
        v2
          ? {
              url: docsPath(true, accountId, `/${documentId}/refresh-urls`),
              method: 'POST',
            }
          : {
              // v1 has no refresh endpoint — its streaming-proxy URLs don't
              // expire. Hit the v1 GET single-document route so callers receive
              // a DocumentApiResponse with files[].viewUrls populated (the v1
              // transform emits a single `original`-type entry pointing at the
              // streaming proxy). preview/thumbnail/gallery variants are absent
              // under v1 — callers using getViewUrl(...) for those types will
              // get `undefined`, which is the same degradation as when variant
              // generation has not yet run for a v2 document.
              url: docsPath(false, accountId, `/${documentId}`),
              method: 'GET',
            },
    }),
    /**
     * Push-based live update for image variants (thumbnails/previews).
     *
     * After a v2 upload resolves on `complete`, its documents render the
     * original image as a placeholder; the worker is still generating variant
     * URLs. Mounting this query (keyed by the upload `jobId`) reopens the
     * upload-progress SSE and, on `imageVariantsReady`, invalidates the shared
     * `getDocuments` list so every thumbnail surface refetches and the variants
     * pop in — no manual refresh.
     *
     * The `queryFn` returns immediately; all behavior lives in
     * `onCacheEntryAdded`, mirroring the SSE lifecycle of `createSSECacheHandler`
     * (await cacheDataLoaded → open EventSource → await cacheEntryRemoved →
     * close) but using `dispatch(invalidateTags(...))` and terminal-event /
     * timeout handling, which the generic helper does not support.
     */
    watchDocumentVariants: build.query<null, { accountId: string; jobId: string }>({
      queryFn: () => ({ data: null }),
      // The payload is always null; nothing to cache. Drop the entry as soon as
      // the last subscriber unmounts so the SSE closes promptly.
      keepUnusedDataFor: 0,
      onCacheEntryAdded: async ({ accountId, jobId }, { cacheDataLoaded, cacheEntryRemoved, dispatch }) => {
        if (typeof EventSource === 'undefined') {
          // No SSE available (e.g. SSR/tests without a stub). Variants still
          // land via refetch-on-view; nothing to watch here.
          return;
        }

        let eventSource: EventSource | null = null;
        let timeout: ReturnType<typeof setTimeout> | undefined;

        const close = () => {
          if (timeout) clearTimeout(timeout);
          eventSource?.close();
          eventSource = null;
        };

        try {
          await cacheDataLoaded;

          const url = `/api/v2/accounts/${accountId}/documents/upload-progress?jobId=${encodeURIComponent(jobId)}`;
          eventSource = new EventSource(url, { withCredentials: true });

          // Backstop: never leave the stream open indefinitely.
          timeout = setTimeout(close, SSE_TIMEOUT_MS);

          eventSource.onmessage = (e: MessageEvent) => {
            try {
              const event = JSON.parse(e.data) as DocumentUploadEvent;
              if (event.type === DocumentUploadEventType.imageVariantsReady) {
                dispatch(documentsApi.util.invalidateTags([{ type: 'DocumentDocument', id: 'LIST' }]));
                close();
              } else if (event.type === DocumentUploadEventType.failed) {
                close();
              }
            } catch {
              // Ignore malformed messages
            }
          };

          eventSource.onerror = () => {
            // Refetch-on-view is the fallback; just stop watching.
            close();
          };
        } catch {
          // cacheDataLoaded rejected — entry removed before data loaded.
        }

        await cacheEntryRemoved;
        close();
      },
    }),
  }),
});

// Internal hooks bound directly to the RTK endpoints. Each takes the explicit
// `v2` arg; the public exports below read the LaunchDarkly flag and inject it.
const {
  useGetDocumentByAccountAndDocumentIdQuery: useGetDocumentByAccountAndDocumentIdQueryInternal,
  useLazyGetDocumentsQuery: useLazyGetDocumentsQueryInternal,
  useLazyGetDocumentByAccountAndDocumentIdQuery: useLazyGetDocumentByAccountAndDocumentIdQueryInternal,
  useUpdateDocumentStatusMutation: useUpdateDocumentStatusMutationInternal,
  useUpdateDocumentTypeMutation: useUpdateDocumentTypeMutationInternal,
  useArchiveDocumentMutation: useArchiveDocumentMutationInternal,
  useUploadDocumentMutation: useUploadDocumentMutationInternal,
  useRefreshDocumentUrlsMutation: useRefreshDocumentUrlsMutationInternal,
} = documentsApi;

/**
 * Subscribe to image-variant readiness for an upload `jobId`. The query is
 * inherently v2-only (it watches the v2 upload-progress SSE), so it needs no
 * LaunchDarkly v2 injection. Pass `{ skip: !jobId }` until a jobId is known.
 */
export const useWatchDocumentVariants = documentsApi.useWatchDocumentVariantsQuery;

const withV2 = <T extends object>(args: T | symbol, v2: boolean): T | symbol =>
  typeof args === 'symbol' ? args : { ...args, v2 };

export const useGetDocumentByAccountAndDocumentIdQuery = ((
  args: Parameters<typeof useGetDocumentByAccountAndDocumentIdQueryInternal>[0],
  options?: Parameters<typeof useGetDocumentByAccountAndDocumentIdQueryInternal>[1],
) => {
  const v2 = useDocumentsV2Enabled();
  return useGetDocumentByAccountAndDocumentIdQueryInternal(withV2(args as any, v2), options);
}) as typeof useGetDocumentByAccountAndDocumentIdQueryInternal;

export const useLazyGetDocumentsQuery = () => {
  const v2 = useDocumentsV2Enabled();
  const [trigger, result, info] = useLazyGetDocumentsQueryInternal();
  const wrapped = useCallback(
    (args: Parameters<typeof trigger>[0], preferCacheValue?: boolean) =>
      trigger(withV2(args as any, v2), preferCacheValue),
    [trigger, v2],
  ) as typeof trigger;
  return [wrapped, result, info] as ReturnType<typeof useLazyGetDocumentsQueryInternal>;
};

export const useLazyGetDocumentByAccountAndDocumentIdQuery = () => {
  const v2 = useDocumentsV2Enabled();
  const [trigger, result, info] = useLazyGetDocumentByAccountAndDocumentIdQueryInternal();
  const wrapped = useCallback(
    (args: Parameters<typeof trigger>[0], preferCacheValue?: boolean) =>
      trigger(withV2(args as any, v2), preferCacheValue),
    [trigger, v2],
  ) as typeof trigger;
  return [wrapped, result, info] as ReturnType<typeof useLazyGetDocumentByAccountAndDocumentIdQueryInternal>;
};

export const useUpdateDocumentStatusMutation = (() => {
  const v2 = useDocumentsV2Enabled();
  const [trigger, result] = useUpdateDocumentStatusMutationInternal();
  const wrapped = useCallback(
    (args: Parameters<typeof trigger>[0]) => trigger({ ...args, v2 }),
    [trigger, v2],
  ) as typeof trigger;
  return [wrapped, result] as ReturnType<typeof useUpdateDocumentStatusMutationInternal>;
}) as typeof useUpdateDocumentStatusMutationInternal;

export const useUpdateDocumentTypeMutation = (() => {
  const v2 = useDocumentsV2Enabled();
  const [trigger, result] = useUpdateDocumentTypeMutationInternal();
  const wrapped = useCallback(
    (args: Parameters<typeof trigger>[0]) => trigger({ ...args, v2 }),
    [trigger, v2],
  ) as typeof trigger;
  return [wrapped, result] as ReturnType<typeof useUpdateDocumentTypeMutationInternal>;
}) as typeof useUpdateDocumentTypeMutationInternal;

export const useArchiveDocumentMutation = (() => {
  const v2 = useDocumentsV2Enabled();
  const [trigger, result] = useArchiveDocumentMutationInternal();
  const wrapped = useCallback(
    (args: Parameters<typeof trigger>[0]) => trigger({ ...args, v2 }),
    [trigger, v2],
  ) as typeof trigger;
  return [wrapped, result] as ReturnType<typeof useArchiveDocumentMutationInternal>;
}) as typeof useArchiveDocumentMutationInternal;

export const useUploadDocumentMutation = (() => {
  const v2 = useDocumentsV2Enabled();
  const [trigger, result] = useUploadDocumentMutationInternal();
  const wrapped = useCallback(
    (args: Parameters<typeof trigger>[0]) => trigger({ ...args, v2 }),
    [trigger, v2],
  ) as typeof trigger;
  return [wrapped, result] as ReturnType<typeof useUploadDocumentMutationInternal>;
}) as typeof useUploadDocumentMutationInternal;

export const useRefreshDocumentUrlsMutation = (() => {
  const v2 = useDocumentsV2Enabled();
  const [trigger, result] = useRefreshDocumentUrlsMutationInternal();
  const wrapped = useCallback(
    (args: Parameters<typeof trigger>[0]) => trigger({ ...args, v2 }),
    [trigger, v2],
  ) as typeof trigger;
  return [wrapped, result] as ReturnType<typeof useRefreshDocumentUrlsMutationInternal>;
}) as typeof useRefreshDocumentUrlsMutationInternal;

export const useGetDocumentsQuery = (
  args: {
    accountId: string;
    documentTypeFilter?: Array<AllDocumentTypes>;
    documentStatusFilter?: Array<DocumentStatus>;
    sort?: DocumentSortField;
  },
  options?: { skip?: boolean },
): ReturnType<typeof documentsApi.endpoints.getDocuments.useQuery> => {
  const { accountId, documentTypeFilter, documentStatusFilter, sort } = args;
  const v2 = useDocumentsV2Enabled();

  return documentsApi.endpoints.getDocuments.useQuery(
    { accountId, v2 },
    {
      ...options,
      selectFromResult: ({ data, ...rest }: any) => {
        if (!data) {
          return { data, ...rest };
        }

        const typeSet = documentTypeFilter ? new Set(documentTypeFilter) : null;
        const statusSet = documentStatusFilter ? new Set(documentStatusFilter) : null;
        const needsFiltering = typeSet || statusSet;

        let filteredData = needsFiltering
          ? data.filter((doc: DocumentDocument) => {
              if (typeSet && !typeSet.has(doc.type)) return false;
              if (statusSet && !statusSet.has(doc.status as DocumentStatus)) return false;
              return true;
            })
          : data;

        if (sort && filteredData.length > 0) {
          filteredData = filteredData === data ? [...filteredData] : filteredData;
          filteredData.sort((a: DocumentDocument, b: DocumentDocument) => {
            const aId = typeof a.id === 'string' ? a.id : a.id?.toString() || '';
            const bId = typeof b.id === 'string' ? b.id : b.id?.toString() || '';

            if (sort === DocumentSortField.ID_DESC) {
              return bId.localeCompare(aId);
            } else if (sort === DocumentSortField.ID_ASC) {
              return aId.localeCompare(bId);
            }
            return 0;
          });
        }

        return {
          data: filteredData,
          ...rest,
        };
      },
    },
  );
};
